import { action, toJS } from 'mobx';
import {
  FieldType,
  MobxFieldControl
} from '@app/src/common/components/form/MobxFieldControl';
import { BaseForm } from '@app/src/common/components/form/BaseForm';
import { addEmailValidator } from '@app/src/common/components/form/Validators';
import {
  ConditionType,
  DependenceRule,
  DependenceType,
  IFormField
} from '@app/src/common/model/FormField';
import DynamicFormControl from './DynamicFormControl';
import { FormValidator } from '@app/src/common/model/FormValidator';
import { DateTimeService } from '@app/src/services/DateTimeService';
import {
  FormControlType,
  isDisplayOnlyControl
} from '@app/src/common/model/FormControls';
import { addValidator } from '@app/src/common/components/dynamic-form/ValidatorUtils';

export function addField(target: any, name: string, config: IFormField) {
  if (!target[name]) {
    Object.defineProperty(target, name, {
      enumerable: true,
      configurable: true,
      value: new DynamicFormControl(config)
    });
  }
}

export default class DynamicForm<
  TFields extends string = string
> extends BaseForm {
  [key: string]: any;
  formFields: IFormField[];
  allFields: IFormField[];
  pairedField = new Map<string, string>();
  // db field names targeted by more than one control, e.g. title / otherTitle
  private pairedKeys = new Set<string>();

  constructor(fields: IFormField[]) {
    super();
    this.allFields = fields;
    this.formFields = this.excludeLayoutFields(fields);
    const keyCount = new Map<string, number>();
    this.formFields.forEach(field => {
      if (!field.isInfoField) {
        addField(this, field.name, field);
        this.addValidators(field);
        const key = this.getDbKey(field);
        keyCount.set(key, (keyCount.get(key) ?? 0) + 1);
      }
      if (field.dbName) {
        this.pairedField.set(field.dbName, field.name);
      }
    });
    keyCount.forEach((count, key) => {
      if (count > 1) {
        this.pairedKeys.add(key);
      }
    });
  }

  getControl(name: TFields): DynamicFormControl | undefined {
    const control = this[name as string];
    return control instanceof DynamicFormControl ? control : undefined;
  }

  hasField(name: TFields): boolean {
    return this[name as string] instanceof DynamicFormControl;
  }

  @action
  getRawObject<T>(): T {
    const controls = Object.keys(this)
      .filter(
        key =>
          this[key] instanceof MobxFieldControl &&
          !isDisplayOnlyControl(
            (this[key] as DynamicFormControl).config.control
          )
      )
      .filter(key => {
        const skipped = this[key].isSkipped;
        if (!skipped) {
          return true;
        }
        if ((this[key] as DynamicFormControl).config.isAsset) {
          return this.checkHideDependency(
            (this[key] as DynamicFormControl).config
          );
        } else {
          return false;
        }
      })
      .filter(key => {
        const config = (this[key] as DynamicFormControl).config;
        return !(
          this.isPairedField(config) && this.checkHideDependency(config)
        );
      });

    return toJS(
      controls.reduce((object: any, key: string) => {
        const control = this[key] as DynamicFormControl;
        const config = control.config;
        if (config.dbName) {
          key = config.dbName;
        }
        const value = this.resolveControlValue(control);
        this.assignControlValue(object, key, config, value);
        return object;
      }, {}) as T
    );
  }

  private resolveControlValue(control: DynamicFormControl): any {
    const config = control.config;
    let value =
      control.value instanceof Array
        ? control.value
            .slice()
            .map(item => (item instanceof Object ? toJS(item) : item))
        : control.value instanceof Object
          ? toJS(control.value)
          : control.value;

    if (this.checkHideDependency(config)) {
      return this.getHiddenFieldValue(config);
    }

    if (value && config.type === FieldType.DATE) {
      value = DateTimeService.reduceTimeZone(value).toISOString();
    }
    if (
      config.type === FieldType.ARRAY &&
      [FormControlType.AUTOCOMPLETE, FormControlType.SELECT].includes(
        config.control
      )
    ) {
      value = value ? [value] : [];
    }
    return value;
  }

  private getHiddenFieldValue(config: IFormField): any {
    if (!config.isAsset) {
      return undefined;
    }
    if (config.type === FieldType.BOOLEAN) {
      return false;
    }
    if (config.type === FieldType.ARRAY) {
      return [];
    }
    return '';
  }

  private assignControlValue(
    object: any,
    key: string,
    config: IFormField,
    value: any
  ): void {
    if (!config.isAsset) {
      object[key] = value;
      return;
    }
    if ([FieldType.BOOLEAN, FieldType.ARRAY].includes(config.type)) {
      value = JSON.stringify(value);
    } else if (config.type === FieldType.NUMBER) {
      value = value !== '' ? JSON.stringify(value) : value;
    }
    object.assets = { ...object.assets, [key]: value };
  }

  private getDbKey(config: IFormField): string {
    return config.dbName || config.name;
  }

  // two controls may be bound to one db field (see dbName, e.g. title / otherTitle);
  // in that case the visible one owns the value and the hidden one must not clear it
  private isPairedField(config: IFormField): boolean {
    return this.pairedKeys.has(this.getDbKey(config));
  }

  checkHideDependency(config: IFormField) {
    const { dependOn } = config;
    if (!dependOn) {
      return false;
    }
    const { value, type, rule = DependenceRule.EQUAL, condition } = dependOn;
    const dependValue = this[dependOn.field]?.value;
    let isHidden = false;
    if (type === DependenceType.HIDE) {
      if (condition === ConditionType.EMPTY && !dependValue) {
        isHidden = true;
      } else if (rule === DependenceRule.EQUAL && dependValue === value) {
        isHidden = true;
      } else if (rule === DependenceRule.NOT_EQUAL && dependValue !== value) {
        isHidden = true;
      }
    }
    return isHidden;
  }

  @action
  apply(changes: any): void {
    this.setValue(changes, true, false, false);
  }

  @action
  applyWithAssets(data: any): void {
    const { assets, ...rest } = data || {};
    this.apply({ ...rest, ...assets });
  }

  @action
  init(changes: any): void {
    this.setValue(changes, false, false, false);
  }

  @action
  setValue(
    changes: any,
    isTouched: boolean = true,
    triggerChange: boolean = false,
    needClean: boolean = true
  ): void {
    if (needClean) {
      this.clear();
    }
    Object.keys(changes).forEach(key => {
      const control = this[key] as DynamicFormControl;
      let value = changes[key];
      if (
        control?.config.isAsset &&
        value &&
        (control.config.type === FieldType.BOOLEAN ||
          value.includes('[') ||
          value.includes('{'))
      ) {
        value = JSON.parse(value);
      }
      if (control?.config.type === FieldType.DATE && value) {
        value = DateTimeService.parseDateAccordingTimeZone(value);
      }
      if (
        control?.config.type === FieldType.ARRAY &&
        [FormControlType.AUTOCOMPLETE, FormControlType.SELECT].includes(
          control?.config.control
        )
      ) {
        value = value ? value[0] : '';
      }
      if (this.pairedField.has(key)) {
        const pairedControl = this[
          this.pairedField.get(key)!
        ] as DynamicFormControl;
        pairedControl?.setValue(value, isTouched, triggerChange);
      }
      control?.setValue(value, isTouched, triggerChange);
    });
    this.setServiceFieldValue(changes, isTouched, triggerChange);
  }

  @action
  setServiceFieldValue(
    changes: any,
    isTouched: boolean = true,
    triggerChange: boolean = false
  ) {
    const serviceFields = this.allFields.filter(
      (field: IFormField) => field.serviceField
    );
    if (!serviceFields?.length) {
      return;
    }
    serviceFields.forEach((field: IFormField) => {
      const { dependOn } = field;
      const control = this[field.name] as DynamicFormControl;
      if (control && dependOn) {
        const dependValue = changes[dependOn.field];
        if (dependOn.condition === ConditionType.EMPTY && !dependValue) {
          control?.setValue(dependOn.value, isTouched, triggerChange);
        } else if (
          dependOn.condition === ConditionType.NOT_EMPTY &&
          !!dependValue
        ) {
          control?.setValue(dependOn.value, isTouched, triggerChange);
        }
      }
    });
  }

  addValidators(field: IFormField) {
    if (field.required) {
      addValidator(this, field, { type: FormValidator.REQUIRED });
    }
    field.validators?.forEach(validator =>
      addValidator(this, field, validator)
    );
    if (field.type === FieldType.EMAIL) {
      addEmailValidator(this, field.name);
    }
  }

  protected get _fields(): MobxFieldControl[] {
    return this.getFieldKeys().map(key => this[key]);
  }

  protected getFieldKeys(): string[] {
    return Object.keys(this).filter(
      key => this[key] instanceof MobxFieldControl
    );
  }

  protected getFieldByKey(key: string): MobxFieldControl | undefined {
    return this[key] instanceof MobxFieldControl ? this[key] : undefined;
  }

  protected excludeLayoutFields(fields: IFormField[]) {
    return fields
      .map(field => (field.type === FieldType.LAYOUT ? field.fields : field))
      .flat() as IFormField[];
  }
}
